import { VEC2, Vector, type AABB, remap, clamp, AABBvsAABB } from 'math'
import type { RangeOptions } from '../input/types'
import type { KnobContext, KnobIntersection, KnobItem } from './types'
import { type VNode } from 'vue'
import type { Clamp } from '@/types/clamp'
import type { Full } from '@/types/macros'
import type { RequiredFields } from '@/utils/macros'
import { unwrapElementRef } from '@/utils/dom/unwrap'
import { KNOB_SIZE } from './constants'
import { isInf } from '@/utils/is'
import { twColor } from '@/utils/style'

export type KnobValueListener = (knob: KnobObject, oldValue: number, nextValue: number) => (void | Promise<void>);

export class KnobObject {
  uid: string
  pos: Vector;
  velocity: Vector = VEC2(0, 0);
  clickPos: Vector = VEC2(0, 0)
  range: RequiredFields<RangeOptions, 'min' | 'max'>
  posRange: AABB
  dragging: boolean
  color: string = 'red'
  _initValue: number = 0
  context?: KnobContext;
  intersecting: boolean = false;
  index: number;

  valueListeners: KnobValueListener[] = [];

  constructor(props: {
    uid: string;
    index: number;
    context?: KnobContext;
    position?: Vector
    range?: RangeOptions
    posRange?: AABB
    value?: number
    color?: string
  }) {
    this.index = props.index;
    this.context = props.context;
    this.uid = props.uid
    this.pos = props.position || VEC2(0, 0)
    this.range = {
      ...(props.range || {}),
      min: props.range?.min || 0,
      max: props.range?.max || 1
    }
    this.posRange = props.posRange || { min: VEC2(0, 0), max: VEC2(0, 0.02) }
    this.dragging = false
    this.color = props.color || twColor('primary-500')

    const val = props.value
    if (typeof val !== 'undefined') {
      this._initValue = val
      this.setValue(val, this.getValueRange());
    }
  }

  isReady() {
    const posRange = this.getPosRange();
    const len = posRange.max - posRange.min;
    if (len <= 2) return false;
    return true;
  }

  clamp() {
    const posBounds = this.getPosRange();
    this.pos.x = clamp(this.pos.x, posBounds.min, posBounds.max);
  }

  addValueListener(listener: KnobValueListener) {
    if (this.valueListeners.includes(listener)) return;
    this.valueListeners.push(listener);
  }

  emitValueChange(oldValue: number, nextValue: number)  {
    this.valueListeners.forEach((fun) => {
      fun(this, oldValue, nextValue);
    })
  }

  setupListeners(node: VNode) {
    const el = unwrapElementRef(node)
    if (!el) return
    el.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      this.onMouseDown(ev)
    })
  }

  onMouseDown(ev: MouseEvent) {
    if (!this.isReady()) return;
    if (this.dragging) return;
    this.velocity = VEC2(0, 0);
    const target = ev.target as HTMLElement
    const rect = target.getBoundingClientRect()
    this.clickPos = VEC2(ev.x, ev.y).sub(VEC2(rect.x, rect.y))
    this.dragging = true
  }

  getIntersections(others: KnobObject[]): KnobIntersection[] {
    const intersectingKnobs = others.filter((kn) => {
      if (kn.uid === this.uid) return false;
       return kn.intersects(this)
    });

    return intersectingKnobs.map(kn => {
      return {
        a: this,
        b: kn,
        delta: 0
      }
    })
  }


  onMouseMove(pos: Vector, delta: Vector) {
    if (!this.dragging) return;
    if (!this.isReady()) return;
    this.velocity = delta.mul(VEC2(1, 0));

    //if (this.intersecting) return;
    const mouseLocal = pos.sub(this.posRange.min).sub(this.clickPos)
    this.setValue(mouseLocal.x, { min: 0, max: this.posRange.max.x });

  }

  setDragging(dragging: boolean) {
    this.dragging = dragging;

    if (!dragging) {
      this.setIntersecting(false);
    }
  }

  setIntersecting(intersecting: boolean) {
    this.intersecting = intersecting;
  }



  updatePositionRange(bound: AABB) {
    this.posRange = bound
    this.setValue(this._initValue, this.getValueRange());
  }

  getValueRange(): Full<Clamp> {
    return {
      min: this.range.min || 0,
      max: this.range.max || 1
    }
  }

  getPosRange(): Full<Clamp> {
    return {
      min: -0.5 * KNOB_SIZE,
      max: this.posRange.max.x - 0.5 * KNOB_SIZE
    }
  }

  getValue() {
    let x = remap(this.pos.x, this.getPosRange(), this.getValueRange());

    // Snap value to steps, if steps are specified
    const st = this.range.step ?? 0;
    if (st > 0) x = Math.ceil(x / st) * st;

    if (isNaN(x) || isInf(x)) x = 0;

    return x;
  }

  setValue(value: number, range: Full<Clamp>, emit: boolean = true): number {
    if (!this.isReady()) return value;
    const oldValue = this.getValue();
    const oldPos = this.pos;

    const posRange = this.getPosRange();
    const valueRange = this.getValueRange();
    const valueWidth = (valueRange.max - valueRange.min);
    let x = remap(value, range, posRange);

    // Snap positionto steps, if steps are specified
    const st = this.range.step ?? 0;
    const vw = valueRange.max - valueRange.min;
    if (st > 0 && this.posRange.max.x > 0 && vw > 0) {
      x = remap(x, posRange, { min: 0, max: 1 });
      const numVertical = Math.max(1, Math.ceil(valueWidth / st));
      const i = Math.round(x * numVertical);
      const n = i / numVertical;
      x = remap(n, { min: 0, max: 1 }, posRange);
    }

    this.pos.x = clamp(x, posRange.min, posRange.max);
    if (isNaN(this.pos.x) || isNaN(this.pos.y)) {
      this.pos = oldPos;
      return value;
    }
    const nextValue = this.getValue();

    if (emit && oldValue !== nextValue) {
      this.emitValueChange(oldValue, nextValue);
      //this.context.onValueChange(this, oldValue, nextValue);
    };

    return nextValue;
  }

  setPosition(x: number, range: Full<Clamp>) {
    if (!this.isReady()) return;
    this.pos.x = remap(x, range, this.getPosRange());
  }

  toItem(): KnobItem {
    return {
      position: this.pos,
      dragging: this.dragging,
      color: this.color,
      uid: this.uid
    }
  }

  getBoundingBox():AABB {
    const k = VEC2(KNOB_SIZE, KNOB_SIZE);
    const min = this.pos;
    const max = min.add(k);
    return { min, max };
  }

  getCenter(): Vector {
    const bound = this.getBoundingBox();
    return bound.min.add(bound.max).scale(0.5);
  }

  intersects(b: KnobObject) {
    if (!this.isReady()) return false;
    return AABBvsAABB(this.getBoundingBox(), b.getBoundingBox());
  }
}
